import { useEffect, useState } from 'react';
import { CircleHelp, ClipboardList, Flame, ShieldCheck, Sparkles, Trophy } from 'lucide-react';
import { navigate } from '@/hooks/useRouter';
import TabbarLayout from '@/components/layout/TabbarLayout';
import PageRenderer from '@/components/page/PageRenderer';
import Loading from '@/components/ui/Loading';
import PgLaunchSheet, { type PgLaunchGame } from '@/components/pg/PgLaunchSheet';
import CqLaunchSheet, { type CqLaunchGame } from '@/components/cq/CqLaunchSheet';
import FgLaunchSheet, { type FgLaunchGame } from '@/components/fg/FgLaunchSheet';
import {
  HomeHero,
  HomeSectionTitle,
  PlatformQuickCard,
  PlatformShowcaseSwiperCard,
  homePlatformThemes,
  type HomeShowcaseAction,
  type HomeShowcaseItem,
} from '@/components/home';
import { getPublishedPage } from '@/services/pageService';
import { getGameRooms, type GameRoom } from '@/services/gameService';
import { getPgConfig, getPgGameList, loadPgSdk, type PgGameItem, type PgPublicConfig } from '@/services/pgService';
import { getCqConfig, getCqGameList, type CqGameItem, type CqPublicConfig } from '@/services/cqService';
import { getFgConfig, getFgGameList, type FgGameItem, type FgPublicConfig } from '@/services/fgService';
import { getBgConfig, type BgPublicConfig } from '@/services/bgService';
import type { WebPage } from '@/types/page';

const defaultCa28Rooms: Pick<GameRoom, 'id' | 'name' | 'icon'>[] = [
  { id: '', name: '一号大厅', icon: undefined },
  { id: '', name: '糖果房间', icon: undefined },
  { id: '', name: '福利房间', icon: undefined },
  { id: '', name: '互动大厅', icon: undefined },
  { id: '', name: '高频大厅', icon: undefined },
  { id: '', name: '极速房间', icon: undefined },
];

export default function Page() {
  const [page, setPage] = useState<WebPage | null>(null);
  const [rooms, setRooms] = useState<GameRoom[]>([]);
  const [pgConfig, setPgConfig] = useState<PgPublicConfig | null>(null);
  const [pgGames, setPgGames] = useState<PgGameItem[]>([]);
  const [cqConfig, setCqConfig] = useState<CqPublicConfig | null>(null);
  const [cqGames, setCqGames] = useState<CqGameItem[]>([]);
  const [fgConfig, setFgConfig] = useState<FgPublicConfig | null>(null);
  const [fgGames, setFgGames] = useState<FgGameItem[]>([]);
  const [bgConfig, setBgConfig] = useState<BgPublicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [launchSheetOpen, setLaunchSheetOpen] = useState(false);
  const [launchGame, setLaunchGame] = useState<PgLaunchGame | null>(null);
  const [cqLaunchSheetOpen, setCqLaunchSheetOpen] = useState(false);
  const [cqLaunchGame, setCqLaunchGame] = useState<CqLaunchGame | null>(null);
  const [fgLaunchSheetOpen, setFgLaunchSheetOpen] = useState(false);
  const [fgLaunchGame, setFgLaunchGame] = useState<FgLaunchGame | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [pageData, roomsData, pgCfg, pgGamesData, cqCfg, cqGamesData, fgCfg, fgGamesData, bgCfg] = await Promise.all([
          getPublishedPage('home').catch(() => null),
          getGameRooms().catch(() => []),
          getPgConfig().catch((e) => { console.error('Failed to load PG config:', e); return null; }),
          getPgGameList().catch(() => []),
          getCqConfig().catch((e) => { console.error('Failed to load CQ config:', e); return null; }),
          getCqGameList().catch(() => []),
          getFgConfig().catch((e) => { console.error('Failed to load FG config:', e); return null; }),
          getFgGameList().catch(() => []),
          getBgConfig().catch((e) => { console.error('Failed to load BG config:', e); return null; }),
        ]);

        setPage(pageData);
        setRooms(roomsData.filter((room) => room.chatOnly !== true));
        setPgConfig(pgCfg);
        setPgGames(pgGamesData);
        setCqConfig(cqCfg);
        setCqGames(cqGamesData);
        setFgConfig(fgCfg);
        setFgGames(fgGamesData);
        setBgConfig(bgCfg);

        if (pgCfg?.enabled && pgCfg.sdkUrl) {
          loadPgSdk(pgCfg.sdkUrl).catch(() => {});
        }
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const handleRoomClick = (roomId: string) => {
    navigate(`/chatroom/${roomId}`);
  };

  const handlePgLobbyClick = () => {
    setLaunchGame({ name: 'PG 游戏大厅', isLobby: true });
    setLaunchSheetOpen(true);
  };

  const handlePgTournamentClick = () => {
    setLaunchGame({ name: 'PG 锦标赛', isTournament: true });
    setLaunchSheetOpen(true);
  };

  const handlePgGameClick = (game: PgGameItem) => {
    setLaunchGame({ name: game.name, iconUrl: game.iconUrl, gameId: game.gameId });
    setLaunchSheetOpen(true);
  };

  const handleCqLobbyClick = () => {
    setCqLaunchGame({ name: 'CQ9 游戏大厅', isLobby: true });
    setCqLaunchSheetOpen(true);
  };

  const handleCqGameClick = (game: CqGameItem) => {
    setCqLaunchGame({ name: game.name, iconUrl: game.iconUrl, gamecode: game.gamecode, gamehall: game.gamehall });
    setCqLaunchSheetOpen(true);
  };

  const handleFgGameClick = (game: FgGameItem) => {
    setFgLaunchGame({ name: game.name, iconUrl: game.iconUrl, gameId: game.gameId });
    setFgLaunchSheetOpen(true);
  };

  const handleBgLobbyClick = () => {
    window.open('/bg-launcher', '_blank');
  };

  const ca28Rooms = rooms.slice(0, 6);

  const openCa28Lobby = () => {
    if (ca28Rooms[0]) {
      handleRoomClick(ca28Rooms[0].id);
    } else {
      navigate('/rooms');
    }
  };

  const platformQuickCards = [
    { theme: homePlatformThemes.ca28, show: true, onClick: openCa28Lobby },
    { theme: homePlatformThemes.pg, show: pgConfig?.enabled, onClick: handlePgLobbyClick },
    {
      theme: homePlatformThemes.cq,
      show: cqConfig?.enabled,
      onClick: cqConfig?.showLobbyLink ? handleCqLobbyClick : () => navigate('/cq-bet-history'),
    },
    { theme: homePlatformThemes.bg, show: bgConfig?.enabled, onClick: handleBgLobbyClick },
    { theme: homePlatformThemes.fg, show: fgConfig?.enabled, onClick: () => navigate('/fg-lobby') },
  ];

  const ca28Items: HomeShowcaseItem[] = (ca28Rooms.length > 0 ? ca28Rooms : defaultCa28Rooms).map((room) => ({
    key: room.id || room.name,
    name: room.name,
    iconUrl: room.icon,
    onClick: () => room.id ? handleRoomClick(room.id) : navigate('/rooms'),
  }));
  const pgItems: HomeShowcaseItem[] = pgGames.map((game) => ({
    key: String(game.gameId),
    name: game.name,
    iconUrl: game.iconUrl,
    onClick: () => handlePgGameClick(game),
  }));
  const cqItems: HomeShowcaseItem[] = cqGames.map((game) => ({
    key: game.gamecode,
    name: game.name,
    iconUrl: game.iconUrl,
    onClick: () => handleCqGameClick(game),
  }));
  const fgItems: HomeShowcaseItem[] = fgGames.map((game) => ({
    key: game.gameId,
    name: game.name,
    iconUrl: game.iconUrl,
    onClick: () => handleFgGameClick(game),
  }));

  const pgActions: HomeShowcaseAction[] = [
    ...(pgConfig?.showTournamentLink ? [{ label: '锦标赛', icon: Trophy, onClick: handlePgTournamentClick }] : []),
    ...(pgConfig?.showBetHistoryLink ? [{ label: '投注记录', icon: ClipboardList, onClick: () => navigate('/pg-bet-history') }] : []),
    { label: '正版验证', icon: ShieldCheck, onClick: () => navigate('/pg-verification') },
  ];

  if (loading) {
    return (
      <TabbarLayout activeTab="home">
        <Loading fullscreen text="加载中..." />
      </TabbarLayout>
    );
  }

  return (
    <TabbarLayout activeTab="home" className="bg-[#06071c]">
      <div className="home-premium-page min-h-dvh overflow-hidden pb-6 text-white">
        <HomeHero />

        <main className="relative z-[1] -mt-1 space-y-3 px-3.5">
          {page && page.components && page.components.length > 0 && (
            <section className="overflow-hidden rounded-[18px] bg-white/8 p-2">
              <PageRenderer page={page} />
            </section>
          )}

          <section className="home-premium-surface overflow-hidden rounded-[18px] bg-[linear-gradient(145deg,rgba(255,255,255,0.96),rgba(235,238,255,0.9)_52%,rgba(255,255,255,0.98))] p-3 text-slate-950 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_12px_36px_rgba(2,6,23,0.34)]">
            <div className="relative z-[1]">
              <HomeSectionTitle icon={Sparkles} title="平台速览" subtitle="不同平台、不同玩法氛围" dark={false} trailing="左右滑动查看更多" />
            </div>
            <div className="no-scrollbar relative z-[1] mt-3 flex gap-2 overflow-x-auto pb-0.5">
              {platformQuickCards.filter((card) => card.show).map(({ theme, onClick }) => (
                <PlatformQuickCard key={theme.key} theme={theme} onClick={onClick} />
              ))}
            </div>
          </section>

          <section>
            <HomeSectionTitle icon={Sparkles} title="游戏平台" subtitle="大厅、活动入口、精选游戏合并展示" dark />
            <div className="mt-2 space-y-2.5">
              <PlatformShowcaseSwiperCard
                theme={homePlatformThemes.ca28}
                primaryAction={{ label: '进入大厅', onClick: openCa28Lobby }}
                secondaryActions={[
                  { label: '热门房间', icon: Flame, onClick: openCa28Lobby },
                  { label: '投注记录', icon: ClipboardList, onClick: () => navigate('/bet-records') },
                  { label: '玩法规则', icon: CircleHelp, onClick: () => navigate('/rooms') },
                ]}
                items={ca28Items}
                itemLabel="热门房间"
              />

              {pgConfig?.enabled && (
                <PlatformShowcaseSwiperCard
                  theme={homePlatformThemes.pg}
                  primaryAction={{ label: '进入大厅', onClick: handlePgLobbyClick }}
                  secondaryActions={pgActions}
                  items={pgItems}
                  itemLabel="热门精选"
                />
              )}

              {cqConfig?.enabled && (
                <PlatformShowcaseSwiperCard
                  theme={homePlatformThemes.cq}
                  primaryAction={{ label: cqConfig.showLobbyLink ? '进入大厅' : '投注记录', onClick: cqConfig.showLobbyLink ? handleCqLobbyClick : () => navigate('/cq-bet-history') }}
                  secondaryActions={[{ label: '投注记录', icon: ClipboardList, onClick: () => navigate('/cq-bet-history') }]}
                  items={cqItems}
                  itemLabel="热门精选"
                />
              )}

              {fgConfig?.enabled && (
                <PlatformShowcaseSwiperCard
                  theme={homePlatformThemes.fg}
                  primaryAction={{ label: '进入大厅', onClick: () => navigate('/fg-lobby') }}
                  secondaryActions={[{ label: '投注记录', icon: ClipboardList, onClick: () => navigate('/fg-bet-history') }]}
                  items={fgItems}
                  itemLabel="热门精选"
                />
              )}

              {bgConfig?.enabled && (
                <PlatformShowcaseSwiperCard
                  theme={homePlatformThemes.bg}
                  primaryAction={{ label: '进入大厅', onClick: handleBgLobbyClick }}
                  secondaryActions={[
                    ...(bgConfig.showBetHistoryLink ? [{ label: '投注记录', icon: ClipboardList, onClick: () => navigate('/bg-bet-history') }] : []),
                  ]}
                  items={[]}
                  itemLabel="视讯大厅"
                />
              )}
            </div>
          </section>
        </main>
      </div>

      <PgLaunchSheet
        open={launchSheetOpen}
        onOpenChange={setLaunchSheetOpen}
        game={launchGame}
        groupId={pgConfig?.groupId}
      />
      <CqLaunchSheet
        open={cqLaunchSheetOpen}
        onOpenChange={setCqLaunchSheetOpen}
        game={cqLaunchGame}
      />
      <FgLaunchSheet
        open={fgLaunchSheetOpen}
        onOpenChange={setFgLaunchSheetOpen}
        game={fgLaunchGame}
      />
    </TabbarLayout>
  );
}
